/* product-form.js — Sneak Freak (AphCom) · product form behaviours
   Elements: <sf-variant-picker> (option → Section Rendering re-render, URL sync, unit toggle),
             <sf-product-form> (Cart AJAX add with loading/error states), <sf-sticky-buy> (bar visibility).
   Requires theme.js (window.theme: cart, pubsub, toast, storage, sectionHtml, fetchText, strings, settings).
   Progressive: without JS the form posts to /cart/add and the radios submit their selection.
   Section Rendering: GET {product.url}?option_values=<ids>&section_id=<id> → swap [data-variant-picker],
   [data-price], [data-buy-buttons], [data-sticky-buy], [data-selected-variant] (one variant JSON, never product JSON).
   Events out (unchanged): theme.pubsub 'variant:change' {sectionId, variant, html} and a bubbling DOM
   'variant:change' {variant}. Event in (request 5.2): document 'gallery:colour' {sectionId, position, colour} from
   product-gallery.js → selectColour() picks that colour (size kept when it exists) and re-renders as a click would.
   Sticky bar (requests 3 + 4): <sf-sticky-buy> follows the CURRENT main action across those swaps; its ADD TO BAG is a
   form="…" submit button, so both CTAs go through the one sf-product-form path (busy state, one add, early confirm). */
'use strict';
(function () {
  const theme = window.theme || (window.theme = {});
  const strings = theme.strings || {};
  const SWAP = ['[data-variant-picker]', '[data-price]', '[data-buy-buttons]', '[data-sticky-buy]', '[data-selected-variant]'];
  const UNIT_KEY = 'units';
  /* the buttons that add this product: the main ADD TO BAG and the sticky bar's — both busy while a variant re-renders */
  const CTA_BUSY = '[data-buy-buttons] [data-add-to-cart], [data-sticky-buy] button[type="submit"]';

  /* Request 4 — one add in flight per product form. Keyed by the form id rather than flagged on the element:
     <sf-product-form> sits inside [data-buy-buttons], which every variant swap re-creates, and a flag on the old
     element let a tap on the fresh one start a second add. */
  const inflight = new Set();
  const idleLabel = new WeakMap();
  /* the form's id attribute. Never form.id: the product form holds <input name="id"> (the variant), and a named
     control shadows the form's own properties, so form.id is that input element, not the id string. */
  function formKey(form) { return (form && form.getAttribute('id')) || ''; }
  /* the form as it is in the document now (a swap replaces it with a twin carrying the same id) */
  function liveForm(form) {
    const id = formKey(form);
    if (!form || form.isConnected || !id) return form;
    return document.getElementById(id) || form;
  }
  /* every button that submits this form, looked up each time (both are re-rendered on variant change): the main
     ADD TO BAG inside it and the sticky bar's form="…" button outside it. button.form resolves either way. */
  function submitters(form) {
    const f = liveForm(form);
    if (!f) return [];
    return Array.from(document.querySelectorAll('button[type="submit"]')).filter((b) => b.form === f);
  }
  /* honest feedback on whichever button was tapped AND its twin: spinner (.is-loading, which also stops further
     pointer taps), aria-busy, and "Adding…" for screen readers (the label is faded under the spinner).
     An add can now finish before the size re-render it was sent during (see _submit): the spinner then stays until
     the re-render lands, as it would for any size change (sf-variant-picker.refresh lifts it). */
  function setBusy(form, on) {
    const f = liveForm(form);
    const host = f && f.closest('sf-product-form');
    const rendering = !on && !!(host && rootFor(host, host.dataset.section).querySelector('sf-variant-picker[aria-busy]'));
    submitters(form).forEach((b) => {
      const label = b.querySelector('.btn__label');
      b.classList.toggle('is-loading', on || rendering);
      if (on) {
        b.setAttribute('aria-busy', 'true');
        if (label && strings.adding && !idleLabel.has(b)) { idleLabel.set(b, label.textContent); label.textContent = strings.adding; }
      } else {
        b.removeAttribute('aria-busy');
        if (label && idleLabel.has(b)) { label.textContent = idleLabel.get(b); idleLabel.delete(b); }
      }
    });
  }

  function rootFor(el, sectionId) {
    return (sectionId && document.getElementById('shopify-section-' + sectionId)) || el.closest('[data-section-id], sf-drawer, .pdp, .quick-add') || document;
  }
  function readVariant(root) {
    const node = root.querySelector('[data-selected-variant]');
    if (!node) return null;
    try { return JSON.parse(node.textContent || 'null'); } catch (e) { return null; }
  }
  function withQuery(url, params) {
    const u = new URL(url, window.location.origin);
    Object.entries(params).forEach(([k, v]) => { if (v === null || v === undefined || v === '') u.searchParams.delete(k); else u.searchParams.set(k, v); });
    return u.toString();
  }

  /* ---------------------------------------------------------------- sf-variant-picker */
  if (!customElements.get('sf-variant-picker')) {
    customElements.define('sf-variant-picker', class extends HTMLElement {
      connectedCallback() {
        this._ctrl = new AbortController();
        const { signal } = this._ctrl;
        this.sectionId = this.dataset.section;
        this.root = rootFor(this, this.sectionId);
        this.addEventListener('change', (e) => this._onChange(e), { signal });
        this.addEventListener('click', (e) => this._onUnitClick(e), { signal });
        this._reqId = 0;
        this._initUnits();
        // size-guide → select size
        this._unsub = theme.pubsub && theme.pubsub.subscribe ? theme.pubsub.subscribe('sizeguide:select', (d) => this.selectEu(d && d.eu)) : null;
        document.addEventListener('sizeguide:select', (e) => this.selectEu(e.detail && e.detail.eu), { signal });
        // gallery → colour: the shopper moved the pictures onto another colour (product-gallery.js)
        document.addEventListener('gallery:colour', (e) => this.selectColour(e.detail), { signal });
      }
      disconnectedCallback() {
        this._ctrl.abort();
        if (this._unsub) this._unsub();
        if (this._abort) this._abort.abort();
      }
      selectEu(eu) {
        if (eu === undefined || eu === null) return;
        const input = this.querySelector('input[data-eu="' + String(eu) + '"]');
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      selectedOptionValueIds() {
        const ids = [];
        this.querySelectorAll('fieldset[data-option-position]').forEach((fs) => {
          const checked = fs.querySelector('input[type="radio"]:checked');
          if (checked) { ids.push(checked.dataset.optionValueId); return; }
          const select = fs.querySelector('select[data-option-select]');
          if (select && select.selectedOptions[0]) ids.push(select.selectedOptions[0].dataset.optionValueId);
        });
        return ids.filter(Boolean);
      }
      _onChange(e) {
        const t = e.target;
        if (!t.matches('input[type="radio"], select[data-option-select]')) return;
        this._paint();
        this.refresh(t);
      }
      /* reflect the selection immediately (optimistic): the selected chip / swatch and the option's name beside
         its title, so the colour name never waits for the section request */
      _paint() {
        this.querySelectorAll('input[type="radio"]').forEach((r) => {
          const label = this.querySelector('label[for="' + r.id + '"]');
          if (label) label.classList.toggle('is-selected', r.checked);
        });
        this.querySelectorAll('fieldset[data-option-position]').forEach((fs) => {
          const cur = fs.querySelector('[data-option-current]');
          const v = this._valueOf(fs);
          if (cur && v !== null && cur.textContent !== v) cur.textContent = v;
        });
      }
      _valueOf(fs) {
        const r = fs.querySelector('input[type="radio"]:checked');
        if (r) return r.value;
        const s = fs.querySelector('select[data-option-select]');
        return s ? s.value : null;
      }
      _setValue(fs, value) {
        const r = Array.from(fs.querySelectorAll('input[type="radio"]')).find((x) => x.value === value);
        if (r) { r.checked = true; return true; }
        const s = fs.querySelector('select[data-option-select]');
        if (s && Array.from(s.options).some((o) => o.value === value)) { s.value = value; return true; }
        return false;
      }
      /* Gallery → variant (request 5.2): select the colour whose photo the shopper moved to. The size is kept when
         that colour has it; otherwise the colour's first size in chip order is taken (likewise any other option).
         The combinations come from [data-variant-options] (every variant's option values). Nothing is dispatched
         back to the gallery beyond the usual variant:change, which the gallery compares against what it shows. */
      selectColour(d) {
        if (!d || d.sectionId !== this.sectionId || !d.position) return;
        const fs = this.querySelector('fieldset[data-option-position="' + d.position + '"]');
        if (!fs || this._valueOf(fs) === d.colour) return;
        const plan = this._planFor(Number(d.position), d.colour);
        if (!this._setValue(fs, d.colour)) return;
        plan.forEach(([pos, value]) => {
          const other = this.querySelector('fieldset[data-option-position="' + pos + '"]');
          if (other) this._setValue(other, value);
        });
        this._paint();
        this.refresh(null);
      }
      _planFor(pos, colour) {
        let combos = null;
        try { const n = this.querySelector('script[data-variant-options]'); combos = n ? JSON.parse(n.textContent) : null; } catch (e) { combos = null; }
        if (!Array.isArray(combos)) return [];
        const sets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
        const cur = {};
        sets.forEach((f) => { cur[f.dataset.optionPosition] = this._valueOf(f); });
        const candidates = combos.filter((o) => Array.isArray(o) && o[pos - 1] === colour);
        if (!candidates.length) return [];
        const others = sets.map((f) => Number(f.dataset.optionPosition)).filter((p) => p !== pos);
        if (candidates.some((o) => others.every((p) => o[p - 1] === cur[p]))) return [];
        const sizeSet = sets.find((f) => f.hasAttribute('data-size-option'));
        const sizePos = sizeSet ? Number(sizeSet.dataset.optionPosition) : 0;
        // display order of each option's values (chip order for sizes)
        const order = {};
        sets.forEach((f) => {
          const vals = Array.from(f.querySelectorAll('input[type="radio"]')).map((x) => x.value);
          const s = f.querySelector('select[data-option-select]');
          order[f.dataset.optionPosition] = vals.length ? vals : (s ? Array.from(s.options).map((o) => o.value) : []);
        });
        const rank = (o) => {
          const keys = [];
          const ps = sizePos ? [sizePos].concat(others.filter((p) => p !== sizePos)) : others;
          ps.forEach((p) => {
            keys.push(o[p - 1] === cur[p] ? 0 : 1);
            const at = (order[p] || []).indexOf(o[p - 1]);
            keys.push(at < 0 ? 9999 : at);
          });
          return keys;
        };
        const best = candidates.map((o) => ({ o, k: rank(o) })).sort((a, b) => {
          for (let i = 0; i < a.k.length; i++) if (a.k[i] !== b.k[i]) return a.k[i] - b.k[i];
          return 0;
        })[0].o;
        return others.filter((p) => best[p - 1] !== cur[p] && best[p - 1] !== undefined).map((p) => [p, best[p - 1]]);
      }
      /* The variant the current selection names, known before the section request answers: the combination is found
         in [data-variant-options] (every variant's option values) and read from [data-variant-ids] ([id, available],
         same order). null when it cannot be told apart (data missing, an option without a value, no such variant). */
      _resolve() {
        let combos = null, ids = null;
        try {
          combos = JSON.parse(this.querySelector('script[data-variant-options]').textContent);
          ids = JSON.parse(this.querySelector('script[data-variant-ids]').textContent);
        } catch (e) { return null; }
        if (!Array.isArray(combos) || !Array.isArray(ids) || combos.length !== ids.length) return null;
        const cur = Array.from(this.querySelectorAll('fieldset[data-option-position]')).map((f) => [Number(f.dataset.optionPosition), this._valueOf(f)]);
        if (!cur.length || cur.some(([, v]) => v === null)) return null;
        const i = combos.findIndex((o) => Array.isArray(o) && o.length === cur.length && cur.every(([p, v]) => o[p - 1] === v));
        const hit = i < 0 ? null : ids[i];
        return Array.isArray(hit) && hit[0] ? { id: Number(hit[0]), available: hit[1] === true } : null;
      }
      /* while a re-render runs: the variant it is fetching (sf-product-form sends a tap with it at once) */
      pendingVariant() {
        return this.hasAttribute('aria-busy') ? this._pending || null : null;
      }
      async refresh(changed) {
        const ids = this.selectedOptionValueIds();
        if (!ids.length) return;
        this._pending = this._resolve();
        const url = withQuery(this.dataset.url, { option_values: ids.join(','), section_id: this.sectionId });
        const reqId = ++this._reqId;
        if (this._abort) this._abort.abort();
        this._abort = new AbortController();
        this.setAttribute('aria-busy', 'true');
        this.root.querySelectorAll(CTA_BUSY).forEach((b) => b.classList.add('is-loading'));
        let swapped = false;
        try {
          const res = await fetch(url, { signal: this._abort.signal, headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error('section ' + res.status);
          const html = await res.text();
          if (reqId !== this._reqId) return; // stale
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const focusedId = changed && changed.id;
          const qty = this._quantity();
          SWAP.forEach((sel) => {
            const next = doc.querySelector(sel);
            const cur = this.root.querySelector(sel);
            if (next && cur) {
              if (sel === '[data-variant-picker]') {
                // keep the unit toggle state: copy the new grid content only
                cur.innerHTML = next.innerHTML;
              } else if (sel === '[data-selected-variant]') {
                cur.textContent = next.textContent;
              } else {
                cur.replaceWith(next);
              }
            }
          });
          const variant = readVariant(this.root);
          this._keepQuantity(qty);
          if (focusedId) { const again = document.getElementById(focusedId); if (again) again.focus({ preventScroll: true }); }
          this._applyUnits();
          if (this.dataset.updateUrl === 'true' && variant && variant.id && window.history && window.history.replaceState) {
            window.history.replaceState({}, '', withQuery(window.location.href, { variant: variant.id }));
          }
          swapped = true;
          if (theme.pubsub) theme.pubsub.publish('variant:change', { sectionId: this.sectionId, variant, html: doc });
          this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
        } catch (err) {
          if (err.name === 'AbortError') return;
          if (theme.toast) theme.toast(strings.error_generic || 'Something went wrong. Please try again.', { type: 'error' });
        } finally {
          if (reqId === this._reqId) {
            this.removeAttribute('aria-busy');
            this.root.querySelectorAll(CTA_BUSY).forEach((b) => b.classList.remove('is-loading'));
            const form = this._form();
            // an add still running from before the swap: the fresh buttons say so too (and stay untappable)
            if (form && inflight.has(formKey(form) || 'product-form')) setBusy(form, true);
            this._sendQueued(swapped);
          }
        }
      }
      _form() { return this.root.querySelector('[data-buy-buttons] form'); }
      /* Request 4: a tap on ADD TO BAG (main or sticky) while this re-render is in flight. The hidden id still holds
         the previous variant, so sf-product-form hands the tap here instead of adding the wrong size; it is sent
         once the new variant is in, from the same kind of button. Only the latest re-render sends it (an aborted
         one keeps it for its successor); a failed one, or a variant that cannot be bought, drops it and the buttons
         return to normal — the bar and the main button then show Sold out / Notify me in place of ADD TO BAG.
         Since build br this queue is only the fallback: when pendingVariant() knows the new variant and it is in
         stock, sf-product-form posts it straight away instead of waiting the 400–800ms of the re-render. */
      queueSubmit(form, submitter) {
        this._queued = { formId: formKey(form), fromBar: !!(submitter && form && !form.contains(submitter)) };
      }
      _sendQueued(ok) {
        const q = this._queued;
        if (!q) return;
        this._queued = null;
        const form = (q.formId && document.getElementById(q.formId)) || this._form();
        if (!form) return;
        const variant = readVariant(this.root);
        if (!ok || !variant || !variant.available) { setBusy(form, false); return; }
        const subs = submitters(form).filter((b) => !b.disabled);
        const sub = subs.find((b) => form.contains(b) !== q.fromBar) || subs[0] || null;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(sub);
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
      /* the quantity the shopper set survives the swap (it used to drop back to 1 on every size change), clamped by
         sf-quantity to what the new variant allows; a sold-out variant has no stepper, so there is nothing to carry */
      _quantity() {
        const input = this.root.querySelector('[data-buy-buttons] sf-quantity input[name="quantity"]');
        return input ? parseInt(input.value, 10) || 1 : 1;
      }
      _keepQuantity(n) {
        if (!(n > 1)) return;
        const q = this.root.querySelector('[data-buy-buttons] sf-quantity');
        if (q && q.input) q.value = n;
      }
      /* size units (EU truth, relabel via SFSizing) */
      _initUnits() {
        const saved = theme.storage && theme.storage.get ? theme.storage.get(UNIT_KEY, null) : null;
        this.system = (saved && saved.system) || (theme.settings && theme.settings.sizeUnit) || 'eu';
        this._applyUnits();
      }
      _onUnitClick(e) {
        const btn = e.target.closest('.unit-opt[data-unit-value]');
        if (!btn || !this.contains(btn)) return;
        this.system = btn.dataset.unitValue;
        if (theme.storage && theme.storage.set) theme.storage.set(UNIT_KEY, Object.assign({}, theme.storage.get(UNIT_KEY, {}) || {}, { system: this.system }));
        this._applyUnits();
        document.dispatchEvent(new CustomEvent('sf:unitchange', { detail: { system: this.system } }));
      }
      _applyUnits() {
        const toggle = this.querySelector('[data-unit-toggle]');
        if (!toggle) return;
        toggle.querySelectorAll('.unit-opt').forEach((b) => { const on = b.dataset.unitValue === this.system; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
        const S = window.SFSizing;
        const brand = (this.dataset.vendor || '').toLowerCase();
        this.querySelectorAll('input[data-eu]').forEach((input) => {
          const label = this.querySelector('label[for="' + input.id + '"] .pdp-size__text');
          if (!label) return;
          const eu = input.dataset.eu;
          let text = eu, aria = (strings.sizes_unit || 'EU') + ' ' + eu;
          if (this.system !== 'eu' && S) {
            try {
              const b = S.hasChart && S.hasChart(brand) ? brand : (S.brands && S.brands[0]) || brand;
              const cm = S.toCm(b, 'eu', Number(eu));
              const rec = cm ? S.fromCm(b, cm) : null;
              const key = { uk: 'uk', 'us-m': 'usM', 'us-w': 'usW' }[this.system];
              if (rec && rec[key] !== undefined && rec[key] !== null) { text = String(rec[key]); aria = text + ' (' + (strings.sizes_unit || 'EU') + ' ' + eu + ')'; }
            } catch (err) { /* keep EU */ }
          }
          label.textContent = text;
          input.setAttribute('aria-label', aria);
        });
      }
    });
  }

  /* ---------------------------------------------------------------- sf-product-form
     Request 4 — the main ADD TO BAG and the sticky bar's (a form="…" submit button outside the form) take this one
     path, measured from the tap:
     · Both buttons show the loading state at once (setBusy) — the sticky one used to show nothing for the whole add
       while the off-screen main button spun.
     · One add per form at a time (inflight); further taps meet a busy button and are dropped, never re-sent.
     · A tap during a size/colour re-render adds the NEW variant — it used to post the previous size's id. Build br:
       the picker resolves the new variant from the page data at the size tap (pendingVariant), so an in-stock one
       is posted at once; only an unresolved or sold-out one is still handed to the picker (queueSubmit) and sent,
       or dropped, when the re-render lands. The quantity is read from the form at send time.
     · Success only once Shopify has accepted the add: theme.cart.add calls onAdded as soon as /cart/add.js answers
       200, so the toast, the status line and the restored buttons no longer wait for the follow-up /cart.js read.
       Build br: that answer must list this variant at (at least) the requested quantity, or no success is shown;
       the bag count moves in the same frame (cart:count from theme.cart.add) and the full cart.js read corrects it.
       In drawer mode the drawer still opens after that read, when it has rendered the new lines and its count
       together.
     · A refusal (422, sold out meanwhile, network) is shown where the shopper is: inline under the main button and,
       when that is off screen or the tap came from the bar, as an error toast too. Focus stays on the button, which
       is restored; it only moves to the inline message when that message is on screen (as before). */
  if (!customElements.get('sf-product-form')) {
    customElements.define('sf-product-form', class extends HTMLElement {
      connectedCallback() {
        this._ctrl = new AbortController();
        this.form = this.querySelector('form');
        if (!this.form || !theme.cart) return;
        this.form.addEventListener('submit', (e) => this._submit(e), { signal: this._ctrl.signal });
      }
      disconnectedCallback() { this._ctrl.abort(); }
      get button() { return this.querySelector('[data-add-to-cart]'); }
      _error(msg) {
        const box = this.querySelector('[data-form-error]');
        if (!box) return;
        if (msg) { box.textContent = msg; box.hidden = false; box.focus && box.setAttribute('tabindex', '-1'); box.focus({ preventScroll: true }); }
        else { box.textContent = ''; box.hidden = true; }
      }
      _fail(msg, fromBar) {
        const box = this.querySelector('[data-form-error]');
        const status = this.querySelector('[data-form-status]');
        if (status) status.textContent = msg;
        if (box) { box.textContent = msg; box.hidden = false; }
        const r = box && box.getBoundingClientRect();
        const dockH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sf-dock-h')) || 0;
        const onScreen = !!(r && r.height && r.top >= 0 && r.bottom <= window.innerHeight - dockH);
        if (onScreen && !fromBar) { box.setAttribute('tabindex', '-1'); box.focus({ preventScroll: true }); }
        else if (theme.toast) theme.toast(msg, { type: 'error', tone: 'error', timeout: 4000 });
      }
      /* this element, or its twin when a variant swap replaced it while the add was running */
      _live() {
        const f = liveForm(this.form);
        return (f && f.closest('sf-product-form')) || this;
      }
      async _submit(e) {
        e.preventDefault();
        const form = this.form;
        const key = formKey(form) || 'product-form';
        const submitter = e.submitter || null;
        // which button it was, decided at the tap: once a swap has replaced the buttons, nothing contains the old one
        const fromBar = !!(submitter && !this.contains(submitter));
        if (inflight.has(key)) return;
        const root = rootFor(this, this.dataset.section);
        const picker = root.querySelector('sf-variant-picker[aria-busy]');
        /* a tap during a size/colour re-render: the picker already knows the variant it is fetching. In stock → post
           that id now (the hidden input is set to it; the re-render landing mid-add keeps both buttons busy and the
           confirmation finds the fresh form). Unknown or sold out → queued as before, and the re-render then shows
           Notify me / Sold out. */
        const pending = picker && typeof picker.pendingVariant === 'function' ? picker.pendingVariant() : null;
        /* one add per re-render, as the queue always gave: once this one has been posted, a further tap on the
           still-spinning button (the add can finish before the re-render lands) is dropped, not added again */
        if (pending && pending.sent) return;
        if (picker && !(pending && pending.available) && typeof picker.queueSubmit === 'function') { picker.queueSubmit(form, submitter); setBusy(form, true); return; }
        const idInput = form.querySelector('[data-variant-id]');
        if (pending && pending.available && idInput && !idInput.disabled) idInput.value = String(pending.id);
        if (!idInput || idInput.disabled || !idInput.value) { this._fail(strings.unavailable || 'Unavailable', fromBar); return; }
        // posted now: it also replaces a tap queued earlier in this re-render (e.g. on a sold-out size), so that is not sent again when it lands
        if (pending && pending.available) { pending.sent = true; picker._queued = null; }
        inflight.add(key);
        setBusy(form, true);
        this._error('');
        const variantId = Number(idInput.value);
        const qtyInput = form.querySelector('input[name="quantity"]');
        const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
        /* Success means Shopify's own answer lists this variant at (at least) this quantity — add.js returns each
           line it touched with that line's quantity. Anything else is not reported as added. */
        const took = (items) => Array.isArray(items) && items.some((it) => it && Number(it.variant_id != null ? it.variant_id : it.id) === variantId && Number(it.quantity) >= quantity);
        const cartType = (theme.settings && theme.settings.cartType) || (document.body.dataset.cartType) || 'toast';
        const sections = cartType === 'drawer' ? ['cart-drawer'] : [];
        /* The toast confirms on add.js (onAdded). The drawer confirms once the whole add has landed, as before: its
           own cart:updated handler renders the new lines AND the header count (sf-cart-drawer._afterUpdate) in one
           pass, so it never opens on the new line beside the old count, and its section HTML is applied once, by
           the drawer itself. */
        const early = cartType !== 'drawer';
        let confirmed = false;
        const confirm = () => {
          if (confirmed) return;
          confirmed = true;
          inflight.delete(key);
          setBusy(form, false);
          const host = this._live();
          const status = host.querySelector('[data-form-status]');
          if (status) status.textContent = strings.added_to_bag || 'Added to your bag';
          if (theme.track) theme.track('add_to_cart', { id: String(variantId), quantity });
          const drawer = document.getElementById('cart-drawer');
          if (cartType === 'drawer' && drawer && typeof drawer.open === 'function') drawer.open(submitter && submitter.isConnected ? submitter : host.querySelector('[data-add-to-cart]'));
          else if (theme.toast) theme.toast(strings.added_to_bag || 'Added to your bag', { type: 'success' });
        };
        try {
          const result = await theme.cart.add({ id: variantId, quantity }, { sections, onAdded: early ? (r) => { if (took(r && r.items)) confirm(); } : null });
          if (took(result && result.items)) confirm(); // the drawer, or a theme.cart without onAdded, confirms here, after the count read
          else if (!confirmed) {
            /* a 200 that does not show this variant at this quantity (Shopify took fewer, or something else): no
               success. The bag count and drawer still follow the real cart (cart:updated has been published). */
            inflight.delete(key);
            setBusy(form, false);
            const line = (result && Array.isArray(result.items) ? result.items : []).find((it) => it && Number(it.variant_id != null ? it.variant_id : it.id) === variantId);
            const msg = line && strings.cart_error_quantity ? strings.cart_error_quantity.replace(/\{\{\s*quantity\s*\}\}/g, String(line.quantity)) : strings.error_generic || 'Something went wrong. Please try again.';
            this._live()._fail(msg, fromBar);
          }
          // product:added keeps its contract: the full theme.cart.add result {cart, items, sections}, once the cart is known,
          // and only after an add that was confirmed (never after the no-proof error above)
          if (confirmed) this._live().dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: result }));
        } catch (err) {
          if (confirmed) return; // Shopify took the add; only the count read after it failed (cart:error is published)
          inflight.delete(key);
          setBusy(form, false);
          const msg = (err && (err.description || err.message)) || strings.error_generic || 'Something went wrong. Please try again.';
          this._live()._fail(msg, fromBar);
        }
      }
    });
  }

  /* ---------------------------------------------------------------- sf-sticky-buy
     Request 3 — the bar is up only while the CURRENT main action (ADD TO BAG, or Notify me on a sold-out variant)
     has genuinely scrolled out of view above the screen, and it leaves as soon as that button is back.
     · Which node. [data-buy-buttons] is re-rendered by every option change (sf-variant-picker.refresh replaces it),
       so a node captured once goes stale: detached, its rect reads all zeros, and "bottom <= 0" held the bar open
       for good after the first size pick, over a fully visible ADD TO BAG (merchant screenshot 15.jpg). The action
       is resolved again on every variant:change and whenever the one in hand has left the document; a missing or
       zero-size node never counts as "scrolled past". (This element survives the swap: [data-sticky-buy] is its
       inner .pdp-buybar__inner.)
     · What is measured. The button itself, not the whole [data-buy-buttons] block, whose sold-out line, restock
       note and error box kept the bar waiting ~30px after the button had gone.
     · Where "out of view" starts. Under the sticky header, read live each frame (.section-header-wrap), so the strip
       above it at the top of the page, a header that is not sticky and one that slides away all stay right; the
       visual viewport's top edge (pinch zoom, an on-screen keyboard) counts too. Before, the button was ignored
       while hidden behind the 60–72px header.
     · No flicker. One decision per frame from one state flag, with a band: the bar goes up once the button's bottom
       edge is at or under that line (fully hidden) and comes down once more than BAND px of it shows again; in
       between nothing changes, so a resting finger, a momentum bounce or sub-pixel rounding never toggles it. The
       DOM is written only when the state flips.
     · Triggers, all into that per-frame check: scroll, resize, orientation, visualViewport resize + scroll (mobile
       browser bars, zoom), variant:change (the swap also moves the button: the "Only 3 left" and sold-out lines come
       and go), header:height, a ResizeObserver on the section (content above the button changing with no scroll)
       and an IntersectionObserver on the button as a wake-up for jumps.
     A button not reached yet (below the screen) keeps the bar down, as before: it appears after scrolling past the
     buy row. Footer yield, --sf-buybar-h and the `hidden` attribute utility-dock.js counts are kept; the bar now
     really slides in (hidden is lifted a frame's layout before is-open) and out (hidden once it has gone). */
  if (!customElements.get('sf-sticky-buy')) {
    const BAND = 8; // px of the main button back on screen before the bar leaves
    customElements.define('sf-sticky-buy', class extends HTMLElement {
      connectedCallback() {
        this._ctrl = new AbortController();
        const { signal } = this._ctrl;
        this.root = rootFor(this, this.dataset.section);
        this._shown = false;
        this._yielding = this.classList.contains('is-yielding');
        this._raf = 0;
        const schedule = () => this._schedule();
        /* publish the bar's height (--sf-buybar-h on <html>) so fixed neighbours — the customer chat launcher, the
           bag toast — sit above it, whatever its content wraps to */
        if ('ResizeObserver' in window) {
          this._ro = new ResizeObserver(() => { const h = this.offsetHeight; if (h) document.documentElement.style.setProperty('--sf-buybar-h', h + 'px'); });
          this._ro.observe(this);
          if (this.root.nodeType === 1) { this._rootRo = new ResizeObserver(schedule); this._rootRo.observe(this.root); }
        }
        if ('IntersectionObserver' in window) this._io = new IntersectionObserver(schedule, { threshold: [0, 1] });
        this._bind();
        const passive = { passive: true, signal };
        window.addEventListener('scroll', schedule, passive);
        window.addEventListener('resize', schedule, passive);
        window.addEventListener('orientationchange', schedule, passive);
        const vv = window.visualViewport;
        if (vv) { vv.addEventListener('resize', schedule, passive); vv.addEventListener('scroll', schedule, passive); }
        // the picker swaps the markup first, then dispatches this (bubbling) — the new button is in the DOM by now
        this.root.addEventListener('variant:change', () => { this._bind(); this._schedule(); }, { signal });
        this._unsub = theme.pubsub && theme.pubsub.subscribe ? theme.pubsub.subscribe('header:height', schedule) : null;
        this.addEventListener('transitionend', (e) => { if (e.target === this && e.propertyName === 'transform' && !this._shown) this.hidden = true; }, { signal });
        this._watchFooter();
        this._sync();
        this._schedule();
      }
      _schedule() { if (!this._raf) this._raf = requestAnimationFrame(() => this._check()); }
      /* the main action as it is in the document now */
      _bind() {
        const buy = this.root.querySelector('[data-buy-buttons]');
        const next = buy ? buy.querySelector('[data-restock-open]') || buy.querySelector('[data-add-to-cart]') || buy : null;
        if (next === this._action) return;
        if (this._io && this._action) this._io.unobserve(this._action);
        this._action = next;
        if (this._io && next) this._io.observe(next);
      }
      /* the top of what the shopper can see: the sticky header's live bottom edge, or the visual viewport's top */
      _clearTop() {
        if (!this._head || !this._head.isConnected) this._head = document.querySelector('.section-header-wrap') || document.querySelector('.site-header');
        const head = this._head ? Math.max(0, this._head.getBoundingClientRect().bottom) : 0;
        const vv = window.visualViewport;
        return Math.max(head, vv ? vv.offsetTop : 0);
      }
      _check() {
        this._raf = 0;
        if (!this._action || !this._action.isConnected) this._bind();
        const a = this._action;
        if (!a) { this._set(false); return; }
        const r = a.getBoundingClientRect();
        if (!r.width && !r.height) return; // not laid out (display: none): no evidence either way, keep the state
        const top = this._clearTop();
        if (r.bottom <= top) this._set(true);
        else if (r.bottom > top + BAND) this._set(false);
      }
      _set(show) {
        if (show === this._shown) return;
        this._shown = show;
        clearTimeout(this._hideT);
        if (show) {
          this.hidden = false;
          void this.offsetWidth; // lay it out at the closed (translated) position first, so the slide-in runs
          this.classList.add('is-open');
        } else {
          this.classList.remove('is-open');
          // hidden on transitionend; this covers no transition (reduced motion, never painted)
          this._hideT = setTimeout(() => { if (!this._shown) this.hidden = true; }, 400);
        }
        this._sync();
      }
      /* out of the tab order whenever it is not really up: sliding out, or stepped aside for the footer */
      _sync() {
        if (this._shown && !this._yielding) this.removeAttribute('inert');
        else this.setAttribute('inert', '');
      }
      /* The bar stops at the footer (Figma 1.11): the moment any of the footer is on screen it slides away and
         leaves the tab order (inert), and it comes back when the footer leaves. This does not depend on the
         utility dock's html.footer-in-view, which only exists on pages where the dock section renders. */
      _watchFooter() {
        const footer = document.querySelector('footer.footer, .footer, body footer');
        if (!footer || footer.contains(this) || !('IntersectionObserver' in window)) return;
        this._fio = new IntersectionObserver((entries) => {
          const on = entries[entries.length - 1].isIntersecting;
          if (on === this._yielding) return;
          this._yielding = on;
          this.classList.toggle('is-yielding', on);
          this._sync();
        }, { threshold: 0 });
        this._fio.observe(footer);
      }
      disconnectedCallback() {
        if (this._ctrl) this._ctrl.abort();
        if (this._unsub) this._unsub();
        [this._io, this._fio, this._ro, this._rootRo].forEach((o) => { if (o) o.disconnect(); });
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = 0;
        this._action = null;
        clearTimeout(this._hideT);
      }
    });
  }

  /* ---------------------------------------------------------------- exclusive PDP accordions
     The PDP accordion items share <details name="…"> so only one is open at a time (Figma 1.14). Browsers
     without exclusive <details> (Safari < 17.2, Chrome < 120) get the same rule here: opening one closes its
     named siblings. Nested, unnamed <details> (Full care notes) are never touched. */
  if (typeof HTMLDetailsElement !== 'undefined' && !('name' in HTMLDetailsElement.prototype)) {
    document.addEventListener('toggle', (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open) return;
      const name = d.getAttribute('name');
      if (!name) return;
      document.querySelectorAll('details[name]').forEach((o) => {
        if (o !== d && o.open && o.getAttribute('name') === name) o.open = false;
      });
    }, true);
  }
})();
